import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function ImportJobs() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["import-jobs", page],
    queryFn: () => get(`/import-jobs?page=${page}&page_size=25`),
    refetchInterval: 3000,
  });
  const overview = useQuery({
    queryKey: ["jobs-overview"],
    queryFn: () => get("/jobs/overview"),
    refetchInterval: 10000,
  });

  const control = useMutation({
    mutationFn: ({ id, action }) => api.post(`/import-jobs/${id}/${action}`),
    onSuccess: (_, { action }) => {
      toast.success(`Import ${action} requested`);
      queryClient.invalidateQueries({ queryKey: ["import-jobs"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const tone = (status) => ({ completed: "completed", failed: "failed", cancelled: "failed",
    blocked: "failed", paused: "paused" }[status] || "running");
  const worker = overview.data?.worker || {};
  const storage = overview.data?.storage || {};

  return (
    <>
      <PageHeader
        testId="import-jobs-page"
        title="Import jobs"
        subtitle="Background CSV processing — chunked, resumable and idempotent"
      >
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500" data-testid="jobs-infra">
          <span>Worker heartbeat: {worker.heartbeat_at ? dt(worker.heartbeat_at) : "unknown"}</span>
          <span>Import concurrency: {worker.import_concurrency ?? "—"}</span>
          <span data-testid="storage-state">
            Object storage: {storage.available ? "available" : `unavailable${storage.error ? ` (${storage.error})` : ""}`}
          </span>
        </div>
      </PageHeader>

      <Section testId="import-jobs-section" title={`${data?.total ?? 0} jobs`}>
        <DataTable
          testId="import-jobs-table"
          rowTestId="import-job-row"
          loading={isLoading}
          columns={[
            { key: "id", label: "Job", render: (r) => <span className="font-mono text-[11px]">{r.id.slice(0, 8)}</span> },
            { key: "list", label: "List", render: (r) => (
              <Link to={`/lists/${r.resource_id}`} className="text-blue-600 hover:underline" data-testid={`job-list-${r.id}`}>
                {r.payload?.filename || r.resource_id?.slice(0, 8)}
              </Link>
            ) },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={tone(r.status)} label={title(r.status)} testId={`job-status-${r.id}`} /> },
            { key: "progress", label: "Progress", render: (r) => (
              <div className="w-40 space-y-1">
                <Progress value={r.progress?.percent || 0} data-testid={`job-progress-${r.id}`} />
                <span className="text-[11px] text-slate-500">
                  {(r.progress?.processed || 0).toLocaleString()} / {(r.progress?.total || 0).toLocaleString()} rows
                </span>
              </div>
            ) },
            { key: "created", label: "Created", render: (r) => (r.progress?.created ?? r.result?.totals?.created ?? 0) },
            { key: "invalid", label: "Invalid", render: (r) => (r.progress?.invalid ?? r.result?.totals?.invalid ?? 0) },
            { key: "attempts", label: "Attempts", render: (r) => `${r.attempts || 0}/${r.max_attempts || 5}` },
            { key: "requested_by_email", label: "Requested by" },
            { key: "created_at", label: "Queued", render: (r) => dt(r.created_at) },
            { key: "error", label: "Error", render: (r) => <span className="line-clamp-1 block max-w-[14rem] text-xs text-rose-600">{r.error || "—"}</span> },
            { key: "actions", label: "", render: (r) => (can("lists", "write") ? (
              <div className="flex gap-1">
                {["processing", "queued"].includes(r.status) ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => control.mutate({ id: r.id, action: "pause" })} data-testid={`job-pause-${r.id}`}>Pause</Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => control.mutate({ id: r.id, action: "cancel" })} data-testid={`job-cancel-${r.id}`}>Cancel</Button>
                  </>
                ) : null}
                {["paused", "blocked"].includes(r.status) ? (
                  <Button size="sm" variant="ghost" onClick={() => control.mutate({ id: r.id, action: "resume" })} data-testid={`job-resume-${r.id}`}>Resume</Button>
                ) : null}
                {["failed", "cancelled"].includes(r.status) ? (
                  <Button size="sm" variant="ghost" onClick={() => control.mutate({ id: r.id, action: "retry" })} data-testid={`job-retry-${r.id}`}>Retry</Button>
                ) : null}
                <Button size="sm" variant="ghost" asChild data-testid={`job-errors-${r.id}`}>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/import-jobs/${r.id}/error-report`} target="_blank" rel="noreferrer">Errors</a>
                </Button>
              </div>
            ) : null) },
          ]}
          rows={data?.items || []}
          empty={{ title: "No import jobs yet", description: "Upload a CSV from Contact Lists to queue an import." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>
    </>
  );
}
